import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { NavigationHandler } from "../interaction/NavigationHandler";
import type { PointerType } from "../interaction/types";
import type { OverlayManager } from "./OverlayManager";
import {
  OverlayType,
  CoordinateSystem,
  type InteractiveOverlay,
  type HandleOverlay,
  type RulerOverlay,
  type HitTestResult,
  type AnyOverlay,
} from "./types";

/**
 * InteractiveLayer - Manages interactive overlay elements with world coordinate tracking.
 *
 * Responsibilities:
 * - Track overlays that use world coordinates
 * - Convert world to screen coordinates on view change
 * - Perform hit testing for pointer events
 * - Manage drag state
 */
export class InteractiveLayer {
  private viewer: DRDrawingViewer;
  private overlayManager: OverlayManager;
  private interactiveOverlays = new Map<string, InteractiveOverlay>();
  private unsubscribeViewChange: (() => void) | null = null;

  // View change listeners
  private viewChangeListeners: Array<() => void> = [];

  // Current drag state
  private _draggedOverlay: InteractiveOverlay | null = null;
  private _draggedId: string | null = null;
  private _dragStartScreen: { x: number; y: number } | null = null;
  private _dragStartWorld: { x: number; y: number; z: number } | null = null;

  constructor(viewer: DRDrawingViewer, overlayManager: OverlayManager) {
    this.viewer = viewer;
    this.overlayManager = overlayManager;
  }

  // ============== Lifecycle ==============

  /**
   * Attach to view change events from NavigationHandler.
   */
  attach(navigationHandler: NavigationHandler): void {
    this.unsubscribeViewChange = navigationHandler.onViewChanged(() => {
      this.updateWorldOverlays();
    });
  }

  /**
   * Detach from view change events.
   */
  detach(): void {
    this.unsubscribeViewChange?.();
    this.unsubscribeViewChange = null;
  }

  /**
   * Subscribe to view change events.
   * @returns Unsubscribe function
   */
  onViewChanged(listener: () => void): () => void {
    this.viewChangeListeners.push(listener);
    return () => {
      const index = this.viewChangeListeners.indexOf(listener);
      if (index >= 0) {
        this.viewChangeListeners.splice(index, 1);
      }
    };
  }

  /**
   * Dispose the interactive layer.
   */
  dispose(): void {
    this.detach();
    this.viewChangeListeners = [];
    this.interactiveOverlays.clear();
    this._draggedOverlay = null;
    this._draggedId = null;
  }

  // ============== Overlay Management ==============

  /**
   * Add an interactive overlay.
   * @returns The overlay ID
   */
  addInteractive(overlay: Omit<InteractiveOverlay, "id">): string {
    // Add to base overlay manager first
    const id = this.overlayManager.add(overlay as Omit<AnyOverlay, "id">);

    // Get the full overlay with ID and store as interactive
    const fullOverlay = this.overlayManager.get(
      id,
    ) as unknown as InteractiveOverlay;
    this.interactiveOverlays.set(id, fullOverlay);

    // If world coordinates, update screen position immediately
    if (overlay.coordinateSystem === CoordinateSystem.World) {
      this.updateOverlayScreenPosition(id, fullOverlay);
    }

    return id;
  }

  /**
   * Remove an interactive overlay.
   */
  removeInteractive(id: string): void {
    this.interactiveOverlays.delete(id);
    this.overlayManager.remove(id);
  }

  /**
   * Get an interactive overlay by ID.
   */
  getInteractive(id: string): InteractiveOverlay | undefined {
    return this.interactiveOverlays.get(id);
  }

  /**
   * Update an interactive overlay.
   */
  updateInteractive(id: string, updates: Partial<InteractiveOverlay>): void {
    const existing = this.interactiveOverlays.get(id);
    if (existing) {
      Object.assign(existing, updates);
      this.overlayManager.update(id, updates as Partial<AnyOverlay>);
    }
  }

  /**
   * Clear all interactive overlays.
   */
  clearInteractive(): void {
    for (const id of this.interactiveOverlays.keys()) {
      this.overlayManager.remove(id);
    }
    this.interactiveOverlays.clear();
  }

  // ============== View Synchronization ==============

  /**
   * Update all world-coordinate overlays to current screen positions.
   * Called automatically when view changes (pan/zoom).
   */
  updateWorldOverlays(): void {
    for (const [id, overlay] of this.interactiveOverlays) {
      if (overlay.coordinateSystem === CoordinateSystem.World) {
        this.updateOverlayScreenPosition(id, overlay);
      }
    }

    // Notify all view change listeners (e.g., RulerController)
    for (const listener of this.viewChangeListeners) {
      listener();
    }

    this.overlayManager.redraw();
  }

  /**
   * Update a single overlay's screen position from its world position.
   */
  private updateOverlayScreenPosition(
    id: string,
    overlay: InteractiveOverlay,
  ): void {
    if (overlay.type === OverlayType.Handle) {
      const handle = overlay as HandleOverlay;
      const screen = this.viewer.worldToScreen(handle.worldPosition);
      handle.screenPosition = screen;

      // Update the underlying overlay with new screen coords for rendering
      this.overlayManager.update(id, {
        x: screen.x,
        y: screen.y,
      } as Partial<AnyOverlay>);
    }
    // Rulers handle their own positioning during render via RulerController
  }

  // ============== Hit Testing ==============

  /**
   * Perform hit test against all interactive overlays.
   * @returns The closest hit result, or null if nothing hit
   */
  hitTest(
    screenPoint: { x: number; y: number },
    pointerType?: PointerType,
  ): HitTestResult | null {
    const worldPoint = this.viewer.screenToWorld(screenPoint);
    let closestHit: HitTestResult | null = null;
    let closestDistance = Infinity;

    for (const [id, overlay] of this.interactiveOverlays) {
      const result = this.hitTestOverlay(
        id,
        overlay,
        screenPoint,
        worldPoint,
        pointerType,
      );
      if (result && result.distance < closestDistance) {
        closestHit = result;
        closestDistance = result.distance;
      }
    }

    return closestHit;
  }

  /**
   * Hit test a single overlay.
   */
  private hitTestOverlay(
    id: string,
    overlay: InteractiveOverlay,
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
    pointerType?: PointerType,
  ): HitTestResult | null {
    const hitRadius = overlay.hitRadius ?? 10;

    switch (overlay.type) {
      case OverlayType.Handle:
        return this.hitTestHandle(
          id,
          overlay as HandleOverlay,
          screenPoint,
          worldPoint,
          hitRadius,
        );
      case OverlayType.Ruler:
        return this.hitTestRuler(
          id,
          overlay as RulerOverlay,
          screenPoint,
          worldPoint,
          pointerType,
        );
      default:
        return null;
    }
  }

  /**
   * Hit test a handle overlay.
   */
  private hitTestHandle(
    id: string,
    handle: HandleOverlay,
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
    hitRadius: number,
  ): HitTestResult | null {
    if (!handle.screenPosition) return null;

    const dx = screenPoint.x - handle.screenPosition.x;
    const dy = screenPoint.y - handle.screenPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= hitRadius) {
      return {
        overlay: handle,
        id,
        screenPoint,
        worldPoint,
        distance,
        zone: "center",
      };
    }
    return null;
  }

  /**
   * Hit test a ruler overlay: its handle, or its bar for the pointers that
   * grab it there (see rulerGrab).
   */
  private hitTestRuler(
    id: string,
    ruler: RulerOverlay,
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
    pointerType?: PointerType,
  ): HitTestResult | null {
    const handleHit = this.hitTestRulerHandle(
      id,
      ruler,
      screenPoint,
      worldPoint,
    );
    if (handleHit) return handleHit;

    if (!pointerType || !ruler.barGrabbedBy?.includes(pointerType)) {
      return null;
    }
    const canvas = this.viewer.canvas;
    const barStart =
      ruler.orientation === "horizontal"
        ? canvas.height - ruler.thickness - ruler.edgeOffset
        : canvas.width - ruler.thickness - ruler.edgeOffset;
    const across =
      (ruler.orientation === "horizontal" ? screenPoint.y : screenPoint.x) -
      barStart;
    if (across < 0 || across > ruler.thickness) return null;
    return {
      overlay: ruler,
      id,
      screenPoint,
      worldPoint,
      distance: Math.abs(across - ruler.thickness / 2),
      zone: "body",
    };
  }

  /**
   * Hit test the position handle of a ruler (unless hidden).
   */
  private hitTestRulerHandle(
    id: string,
    ruler: RulerOverlay,
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
  ): HitTestResult | null {
    if (ruler.showHandle === false) return null;
    const canvas = this.viewer.canvas;
    const thickness = ruler.thickness;
    const offset = ruler.edgeOffset;

    // Handle positions (must match RulerController)
    const handleSize = ruler.handleSize ?? 20;
    const handleRadius = handleSize / 2 + 5; // Hit radius for handles

    if (ruler.orientation === "horizontal") {
      // Calculate ruler Y position (accounting for offset)
      const rulerY = canvas.height - thickness - offset;

      // Check if clicking on the position handle (bottom-left of visible
      // canvas, right of the floating toolbar)
      const hHandleX = (ruler.handleOffsetX ?? 0) + handleSize / 2 + 5;
      const hHandleY = rulerY + thickness / 2;
      const dxHandle = screenPoint.x - hHandleX;
      const dyHandle = screenPoint.y - hHandleY;
      const distToHandle = Math.sqrt(dxHandle * dxHandle + dyHandle * dyHandle);

      if (distToHandle <= handleRadius) {
        return {
          overlay: ruler,
          id,
          screenPoint,
          worldPoint,
          distance: distToHandle,
          zone: "handle",
        };
      }
    } else {
      // Vertical ruler - canvas is now positioned within layout, no offset needed
      const RIGHT_TOOLBAR_OFFSET = 0;
      const rulerX = canvas.width - thickness - RIGHT_TOOLBAR_OFFSET - offset;

      // Check if clicking on the position handle (top of canvas)
      // Canvas is now positioned below header, no offset needed
      const HEADER_OFFSET = 0;
      const vHandleX = rulerX + thickness / 2;
      const vHandleY =
        HEADER_OFFSET + (ruler.handleOffsetY ?? 0) + handleSize / 2 + 5;
      const dxHandle = screenPoint.x - vHandleX;
      const dyHandle = screenPoint.y - vHandleY;
      const distToHandle = Math.sqrt(dxHandle * dxHandle + dyHandle * dyHandle);

      if (distToHandle <= handleRadius) {
        return {
          overlay: ruler,
          id,
          screenPoint,
          worldPoint,
          distance: distToHandle,
          zone: "handle",
        };
      }
    }

    return null;
  }

  // ============== Drag Handling ==============

  /**
   * Handle drag start - begins drag state and calls overlay handler.
   */
  handleDragStart(hit: HitTestResult): void {
    this._draggedOverlay = hit.overlay;
    this._draggedId = hit.id;
    this._dragStartScreen = { ...hit.screenPoint };
    this._dragStartWorld = { ...hit.worldPoint };
    hit.overlay.isDragging = true;

    // Call the overlay's drag handler
    hit.overlay.onDragStart?.(hit);
  }

  /**
   * Handle drag move with pre-snapped coordinates.
   * Use this when snap processing has already been applied.
   */
  handleDragMove(
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
  ): void {
    if (!this._draggedOverlay) return;
    this._draggedOverlay.onDragMove?.(screenPoint, worldPoint);
  }

  /**
   * Handle drag end with pre-snapped coordinates.
   * Use this when snap processing has already been applied.
   */
  handleDragEnd(
    screenPoint: { x: number; y: number },
    worldPoint: { x: number; y: number; z: number },
  ): void {
    if (!this._draggedOverlay) return;

    this._draggedOverlay.onDragEnd?.(screenPoint, worldPoint);

    // Clean up drag state
    this._draggedOverlay.isDragging = false;
    this._draggedOverlay = null;
    this._draggedId = null;
    this._dragStartScreen = null;
    this._dragStartWorld = null;
  }

  /**
   * @deprecated Use handleDragStart instead
   */
  beginDrag(hit: HitTestResult): void {
    this._draggedOverlay = hit.overlay;
    this._draggedId = hit.id;
    this._dragStartScreen = { ...hit.screenPoint };
    this._dragStartWorld = { ...hit.worldPoint };
    hit.overlay.isDragging = true;
  }

  /**
   * @deprecated Use handleDragEnd instead
   */
  endDrag(): void {
    if (this._draggedOverlay) {
      this._draggedOverlay.isDragging = false;
    }
    this._draggedOverlay = null;
    this._draggedId = null;
    this._dragStartScreen = null;
    this._dragStartWorld = null;
  }

  /**
   * Check if currently dragging an overlay.
   */
  get isDragging(): boolean {
    return this._draggedOverlay !== null;
  }

  /**
   * Get the ID of the currently dragged overlay.
   */
  get draggedId(): string | null {
    return this._draggedId;
  }

  /**
   * Get the currently dragged overlay.
   */
  get draggedOverlay(): InteractiveOverlay | null {
    return this._draggedOverlay;
  }

  /**
   * Get the drag start position in screen coordinates.
   */
  get dragStartScreen(): { x: number; y: number } | null {
    return this._dragStartScreen;
  }

  /**
   * Get the drag start position in world coordinates.
   */
  get dragStartWorld(): { x: number; y: number; z: number } | null {
    return this._dragStartWorld;
  }
}
